import React, {useEffect, useState} from 'react';
import {useConnectedUser, UserType} from '../../../context/connected-user.context';
import {RoundStakingStatus, StakingInfo, toStakingType} from '../../../model/round.model';
import {SectionTitle} from '../../../design-system/title';
import {Web3Button} from '../../../design-system/button';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {StakingData, toRoundStatus} from '../../../web3/model/staking.model';
import {timestampToDay} from '../../../web3/mappers/mapper-utils';
import {MyStakesForRound} from '../user-stakes';
import {StakingRoundUserActions} from './staking-actions';

export type StakeRoundDetailFromIdProps = {
    roundId: string;
    userType: UserType;
};
export const StakeRoundDetailFromId: React.FC<StakeRoundDetailFromIdProps> = ({ roundId, userType }) => {
    const cdkContract = useConnectedUser().contract;
    const [round, setRound] = useState<StakingData | undefined>(undefined);

    useEffect(() => {
        if (cdkContract) {
            cdkContract.stakingFacet.getStakingRoundDetails(roundId).then((round) => {
                setRound(round);
            });
        }
    }, [cdkContract, roundId]);

    return (
        <div>
            <SectionTitle title="Associated staking round" />
            {round && <StakingDataView stakingRoundId={roundId} roundDetails={round} userType={userType} />}
        </div>
    );
};

const DisplayData: React.FC<{ title: string; data: string | number }> = ({ title, data }) => {
    return (
        <div>
            <span>{title}: </span>
            <span className="font-bold">{data}</span>
        </div>
    );
};

export type StakeRoundDetailProps = {
    stakingRoundId: string;
    roundDetails: StakingData;
    userType: UserType;
};
export const StakingDataView: React.FC<StakeRoundDetailProps> = (props) => {
    const [withdrawableAmount, setWithdrawableAmount] = useState<number | undefined>(undefined);
    const [calculatedRewards, setCalculatedRewards] = useState<number | undefined>(undefined);
    const connectedUser = useConnectedUser();

    const getWithdrawableAmount = async () => {
        const withdrawableAmount = await connectedUser.contract!.stakingFacet.getWithdrawableAmount(
            props.stakingRoundId,
            connectedUser.connectedUser!.walletAddress
        );
        setWithdrawableAmount(withdrawableAmount);
        toast.info(`Withdrawable amount: ${withdrawableAmount}`);
    };

    const calculateRewards = async () => {
        const calculateReward = await connectedUser.contract!.stakingFacet.doCalculateRewards(
            props.stakingRoundId,
            connectedUser.connectedUser!.walletAddress
        );
        setCalculatedRewards(calculateReward);
        toast.info(`Calculate Reward: ${calculateReward}`);
    };

    return (
        <div>
            <div className="px-4 py-2">
                <DisplayData data={props.stakingRoundId} title="Round" />
                <DisplayData data={props.roundDetails.stakingStatus} title="Status" />
                <DisplayData data={props.roundDetails.stakingType} title="Type" />
                <DisplayData data={props.roundDetails.stakingRewardPercentage} title="Reward percentage" />
                <DisplayData data={props.roundDetails.cliffPeriod.toString()} title="Cliff period" />
                <DisplayData data={props.roundDetails.vestingPeriod.toString()} title="Reward" />
                <DisplayData data={props.roundDetails.createdAt} title="Created at" />
                <DisplayData data={props.roundDetails.endStaking} title="End" />
            </div>

            <div>
                <MyStakesForRound stakingRoundId={props.stakingRoundId} />
            </div>

            <div>
                <SectionTitle title="Actions" />
                <StakingRoundUserActions stackingRoundId={props.stakingRoundId} stackingRound={props.roundDetails} />
                <div className="form">
                    <div className="form-group">
                        <Web3Button text="Calculate your rewards" web3Action={getWithdrawableAmount} type="display" />
                        {withdrawableAmount && <div>Withdrawable amount: {withdrawableAmount}</div>}
                    </div>
                    <div>
                        <div className="form-group">
                            <div>
                                <Web3Button text="Get your withdrawable amount" web3Action={calculateRewards} type="display" />
                            </div>
                            {calculatedRewards && <div>Rewards: {calculatedRewards}</div>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export type ListRoundProps = {
    userType: UserType;
    reloadAllStakingRoundsIndex?: number;
};

export const ListActiveStakings: React.FC<ListRoundProps> = ({ userType, reloadAllStakingRoundsIndex }) => {
    return <ListFilteredStakings status="ON" userType={userType} reloadAllStakingRoundsIndex={reloadAllStakingRoundsIndex} />;
};

export const ListLastActiveStaking: React.FC<ListRoundProps> = ({ userType, reloadAllStakingRoundsIndex }) => {
    return <ListFilteredStakings status="ON" userType={userType} limit={10} reloadAllStakingRoundsIndex={reloadAllStakingRoundsIndex} />;
};

export const ListAllStakings: React.FC<ListRoundProps> = ({ userType, reloadAllStakingRoundsIndex }) => {
    return <ListFilteredStakings userType={userType} reloadAllStakingRoundsIndex={reloadAllStakingRoundsIndex} />;
};

export const StakingDataHeader: React.FC = () => {
    return (
        <tr>
            <th>Round ID</th>
            <th>Status</th>
            <th>Stacking Type</th>
            <th>Reward %</th>
            <th>Cliff</th>
            <th>Vesting</th>
            <th>Created At</th>
            <th>Ending at</th>
            <th>Actions</th>
        </tr>
    );
}

export const StakingDataDisplay: React.FC<{stakingInfo: StakingInfo}> = ({stakingInfo}) => {
    return (
        <tr>
            <td>{stakingInfo.stakingId}</td>
            <td>{toRoundStatus(stakingInfo.stakingDetails.stakingStatus)}</td>
            <td>{toStakingType(stakingInfo.stakingDetails.stakingType)}</td>
            <td>{stakingInfo.stakingDetails.stakingRewardPercentage}</td>
            <td>{stakingInfo.stakingDetails.cliffPeriod}</td>
            <td>{stakingInfo.stakingDetails.vestingPeriod}</td>
            <td>{timestampToDay(stakingInfo.stakingDetails.createdAt).toString()}</td>
            <td>{timestampToDay(stakingInfo.stakingDetails.endStaking).toString()}</td>
            <td></td>
            {/*<td>*/}
            {/*    <Button text="Voir les actions" onClick={() => goToRound(round.stackingId)} type="action" />*/}
            {/*</td>*/}
        </tr>
    )

}

type ListFilteredStakeRoundsProps = ListRoundProps & {
    status?: RoundStakingStatus;
    limit?: number;
};
const ListFilteredStakings: React.FC<ListFilteredStakeRoundsProps> = ({ status, userType, limit, reloadAllStakingRoundsIndex }) => {
    const contract = useConnectedUser().contract;
    const [stakingRounds, setStackingRounds] = useState<StakingInfo[]>([]);
    const navigate = useNavigate();

    const filterRounds = (stakingRounds: StakingInfo[]) => {
        // if (status !== undefined) {
        //     return stakingRounds.filter((round) => round.stakingDetails.stakingStatus === status);
        // }
        return stakingRounds;
    };

    const loadAllStakingRounds = async () => {
        if (contract) {
            const stakingInfos = await contract!.stakingFactoryFacet.getAllStakingDetails();
            setStackingRounds(stakingInfos.slice(0, limit));
        }
    }

    useEffect(() => {
        loadAllStakingRounds();
    }, [contract]);

    useEffect(() => {
        if (reloadAllStakingRoundsIndex) {
            loadAllStakingRounds();
        }
    }, [reloadAllStakingRoundsIndex]);

    const goToRound = (roundId: string) => {
        navigate(`/staking-round/${roundId}?userType=${userType}`);
    };

    return (
        <div>
            <div>
                <table>
                    <thead>
                        <StakingDataHeader />
                    </thead>
                    <tbody>
                        {filterRounds(stakingRounds).map((stakingInfo, index) => (
                            <StakingDataDisplay stakingInfo={stakingInfo} key={index} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
